/* ==========================================================================
   Pawsome Plushies — storefront behavior
   Vanilla JS, no dependencies. Cart drawer + AJAX cart, predictive search,
   mobile nav, sticky header, PDP gallery / sticky ATC, wishlist hearts.
   ========================================================================== */
(function () {
  'use strict';

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const money = (cents) =>
    (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents, window.PP_MONEY_FORMAT)
      : '$' + (cents / 100).toFixed(2);

  /* -------------------------------------------------- Cart drawer */
  const Cart = {
    drawer: null,
    lastSync: 0,

    /* The drawer node is looked up once at boot. Anything that replaces the
       section afterwards (theme editor re-render, an app injecting markup)
       leaves `drawer` pointing at a detached node, and every write then lands
       on nothing. Re-resolve on demand instead of trusting the boot lookup. */
    ensureDrawer() {
      if (this.drawer && this.drawer.isConnected) return this.drawer;
      const found = document.getElementById('CartDrawer');
      if (found && found !== this.drawer) {
        if (this.drawer) console.warn('[cart] #CartDrawer reference was stale — re-resolved.');
        this.drawer = found;
      } else if (!found) {
        console.warn('[cart] #CartDrawer not found in the DOM.');
        this.drawer = null;
      }
      return this.drawer;
    },

    init() {
      this.drawer = document.getElementById('CartDrawer');
      // openers
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-toggle]');
        if (opener) {
          e.preventDefault();
          this.open();
        }
        if (e.target.closest('[data-cart-close]') || e.target.matches('.cart-drawer__overlay')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      /* A bfcache restore (Back on a phone) hands back the DOM exactly as it
         was, cart badge and all, without re-running the server render. Phones
         restore from bfcache far more eagerly than desktop, which is why a
         stale cart reads as a mobile-only bug. */
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.syncIfStale(0);
      });

      /* Same staleness, different route in: the cart was changed in another tab
         or in the Shop app, then this one is brought back to the foreground. */
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.syncIfStale(5000);
      });

      this.bindLineEvents();
      /* A page can load with protection as the only line (the cart page's
         native Remove link reloads the page); tidy that straight away. */
      this.reconcile();
    },
    isOpen() { return this.drawer && this.drawer.classList.contains('is-open'); },
    open() {
      if (!this.ensureDrawer()) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      CartUpsell.pickPair(this.drawer, true);
      const focusable = this.drawer.querySelector('[data-cart-close]');
      if (focusable) focusable.focus();
      /* The drawer's contents are whatever the server rendered when THIS page
         loaded. That is only still true if this page did the add itself. Open
         it from the bottom nav after adding in another tab, or after a bfcache
         restore, and it shows a cart that no longer exists — the empty state
         with a live cart behind it. Sync on every open; the panel is already
         on screen, so this just corrects it a beat later. */
      this.syncIfStale();
    },

    /* Cheap guard so the add path (which already re-rendered the drawer) does
       not immediately fetch the same thing again. */
    syncIfStale(maxAgeMs) {
      const age = Date.now() - this.lastSync;
      if (age < (maxAgeMs == null ? 1500 : maxAgeMs)) return;
      this.refresh(false);
    },
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count < 1;
        el.classList.toggle('is-empty', count < 1);
      });
    },
    async refresh(openAfter) {
      try {
        const res = await fetch(`${window.Shopify ? Shopify.routes.root : '/'}?sections=cart-drawer`);
        const data = await res.json();
        if (data['cart-drawer']) {
          const tmp = document.createElement('div');
          tmp.innerHTML = data['cart-drawer'];
          const fresh = tmp.querySelector('#CartDrawer');
          if (fresh && this.ensureDrawer()) {
            this.drawer.innerHTML = fresh.innerHTML;
            this.mirrorState(fresh);
            this.lastSync = Date.now();
            this.reconcile();
          } else {
            console.warn('[cart] refresh could not render the drawer.');
          }
        }
      } catch (e) {
        console.warn('[cart] refresh failed:', e && e.message);
      }
      if (openAfter) this.open();
    },
    // Copy root state (count + total) off a freshly-rendered #CartDrawer, then
    // re-sync the money-driven UI: header badge, shipping bars, upsell recs.
    mirrorState(fresh) {
      const count = parseInt(fresh.getAttribute('data-item-count') || '0', 10);
      const total = parseInt(fresh.getAttribute('data-cart-total') || '0', 10);
      if (this.drawer) {
        this.drawer.setAttribute('data-item-count', count);
        this.drawer.setAttribute('data-cart-total', total);
        /* Only the drawer's CHILDREN are swapped on a re-render, so the root's
           cart-state attributes must be copied over too, or reconcile() reads
           the state from when the page loaded. */
        ['data-real-count', 'data-protection-qty', 'data-protection-variant'].forEach((a) => {
          if (fresh.hasAttribute(a)) this.drawer.setAttribute(a, fresh.getAttribute(a));
        });
      }
      this.updateCount(count);
      if (window.PawsomeShip) window.PawsomeShip.update(total);
      CartUpsell.enhance(this.drawer);
    },
    /* ---- optimistic helpers -------------------------------------------
       Removing a line and ticking package protection both used to wait on the
       network and then swap the whole section's HTML, which reads as a jump.
       Both outcomes are known the instant the shopper acts, so show them
       straight away and let the request catch up. Anything unexpected rolls
       back to the server's truth via refresh(). */
    reduceMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    },
    subtotalEl() { return this.drawer ? this.drawer.querySelector('[data-subtotal-cents]') : null; },
    currentSubtotal() {
      const el = this.subtotalEl();
      return el ? parseInt(el.getAttribute('data-subtotal-cents') || '0', 10) : 0;
    },
    /* Count the subtotal to its new value so the change is legible instead of
       snapping. Shipping bars follow the same animated value. */
    setSubtotal(cents, animate) {
      const el = this.subtotalEl();
      if (!el) return;
      const from = this.currentSubtotal();
      const to = Math.max(0, cents);
      el.setAttribute('data-subtotal-cents', to);
      const paint = (v) => {
        el.textContent = money(Math.round(v));
        if (window.PawsomeShip) window.PawsomeShip.update(Math.round(v));
      };
      if (!animate || this.reduceMotion() || from === to) { paint(to); return; }
      const start = performance.now(), dur = 320;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        paint(from + (to - from) * eased);
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },
    /* Collapse a line to nothing, then drop it. Height is animated from its
       measured px value because `auto` is not interpolatable. */
    collapseLine(li) {
      return new Promise((resolve) => {
        if (this.reduceMotion()) { li.remove(); resolve(); return; }
        const h = li.getBoundingClientRect().height;
        li.style.overflow = 'hidden';
        li.style.height = h + 'px';
        li.style.transition = 'opacity 200ms ease, height 220ms ease 120ms, margin 220ms ease 120ms, padding 220ms ease 120ms';
        requestAnimationFrame(() => {
          li.style.opacity = '0';
          li.style.height = '0px';
          li.style.marginTop = '0px'; li.style.marginBottom = '0px';
          li.style.paddingTop = '0px'; li.style.paddingBottom = '0px';
        });
        setTimeout(() => { li.remove(); resolve(); }, 360);
      });
    },
    /* ---- cart mutations -------------------------------------------------
       Every change goes through run(): one request at a time, in order, so fast
       taps can never interleave (the old toggle could add protection while a
       removal was still in flight and end up with the cart and the UI
       disagreeing). Lines are addressed by their KEY, never by position:
       Shopify puts a newly added line at the TOP of the cart, so positions
       rendered before a protection add pointed one line too high, and
       "remove" on a product removed the protection line instead. */
    run(task) {
      const next = (this._queue || Promise.resolve()).then(task, task);
      this._queue = next.catch(() => {});
      return next;
    },
    /* Section ids to re-render with each mutation: the drawer always, plus the
       cart page's own section when we are on /cart. */
    sectionIds() {
      const page = document.querySelector('[data-cart-page]');
      const ids = ['cart-drawer'];
      if (page && page.getAttribute('data-section-id')) ids.push(page.getAttribute('data-section-id'));
      return ids.join(',');
    },
    /* Cart requests in flight right now (read by QA; 0 = the cart is settled,
       including the follow-up that tidies a stray protection line). */
    pending: 0,
    async post(path, body) {
      this.pending++;
      try {
        const res = await fetch(`${Shopify.routes.root}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({ sections: this.sectionIds(), sections_url: window.location.pathname }, body)),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || ('HTTP ' + res.status));
        return data;
      } finally {
        this.pending--;
      }
    },
    async removeLineOptimistic(key, li) {
      const lineTotal = parseInt(li.getAttribute('data-line-total') || '0', 10);
      const wasCount = parseInt(this.drawer.getAttribute('data-item-count') || '0', 10);
      const qty = parseInt((li.querySelector('[data-qty-input]') || {}).value || '1', 10) || 1;
      this.collapseLine(li);
      this.setSubtotal(this.currentSubtotal() - lineTotal, true);
      this.updateCount(Math.max(0, wasCount - qty));
      return this.run(async () => {
        try {
          const data = await this.post('cart/change.js', { id: key, quantity: 0 });
          this.applySections(data.sections);
          if (data.item_count != null) this.updateCount(data.item_count);
        } catch (e) {
          console.warn('[cart] remove failed, restoring from server:', e && e.message);
          this.lastSync = 0;
          await this.refresh(false);
        }
        await this.reconcile();
      });
    },
    /* Protection is SET to exactly 1 or 0 with update.js, keyed by variant, so
       a repeated request can never create a second line. Every protection box
       on the page stays disabled until the server has answered. */
    async setProtection(input) {
      const id = parseInt(input.getAttribute('data-variant'), 10);
      const cents = parseInt(input.getAttribute('data-price-cents') || '0', 10);
      const on = input.checked;
      document.querySelectorAll('[data-protection]').forEach((box) => { box.disabled = true; box.checked = on; });
      if (this.drawer) this.setSubtotal(this.currentSubtotal() + (on ? cents : -cents), true);
      return this.run(async () => {
        try {
          const data = await this.post('cart/update.js', { updates: { [id]: on ? 1 : 0 } });
          this.applySections(data.sections);
          if (data.item_count != null) this.updateCount(data.item_count);
        } catch (e) {
          console.warn('[cart] protection toggle failed, restoring from server:', e && e.message);
          this.lastSync = 0;
          await this.refresh(false);
        } finally {
          document.querySelectorAll('[data-protection]').forEach((box) => { box.disabled = false; });
        }
        await this.reconcile();
      });
    },
    /* The cart must never be "protection only", and never hold more than one
       protection. Read off the freshly rendered drawer; acts only when the
       state is wrong, so calling it after every render cannot loop. */
    async reconcile() {
      if (this._reconciling || !this.ensureDrawer()) return;
      const real = parseInt(this.drawer.getAttribute('data-real-count') || '0', 10);
      const prot = parseInt(this.drawer.getAttribute('data-protection-qty') || '0', 10);
      const id = parseInt(this.drawer.getAttribute('data-protection-variant') || '0', 10);
      let want = null;
      if (prot > 0 && real === 0) want = 0;
      else if (prot > 1) want = 1;
      if (want === null || !id) return;
      this._reconciling = true;
      try {
        /* Retried with backoff: a dropped request (flaky network, a 429) must
           not leave a protection-only cart behind. */
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const data = await this.post('cart/update.js', { updates: { [id]: want } });
            this.applySections(data.sections);
            if (data.item_count != null) this.updateCount(data.item_count);
            return;
          } catch (e) {
            console.warn('[cart] could not tidy the protection line, retrying:', e && e.message);
            await new Promise((r) => setTimeout(r, 800 * Math.pow(2, attempt)));
          }
        }
      } finally {
        this._reconciling = false;
      }
    },

    bindLineEvents() {
      document.addEventListener('click', async (e) => {
        /* Only the cart's own controls: they carry a line key. The PDP's
           quantity buttons share data-qty-step but must not touch the cart. */
        const remove = e.target.closest('[data-line-remove][data-line-key]');
        const step = e.target.closest('[data-qty-step][data-line-key]');
        if (remove) {
          e.preventDefault();
          const key = remove.getAttribute('data-line-key');
          const li = remove.closest('.cart-line');
          if (li && this.drawer && this.drawer.contains(li)) await this.removeLineOptimistic(key, li);
          else await this.changeLine(key, 0);
        } else if (step) {
          e.preventDefault();
          const key = step.getAttribute('data-line-key');
          const input = step.parentNode.querySelector('[data-qty-input]');
          let qty = parseInt(input ? input.value : '1', 10);
          qty += step.getAttribute('data-qty-step') === 'up' ? 1 : -1;
          if (qty < 0) qty = 0;
          if (input) input.value = qty;
          await this.changeLine(key, qty);
        }
      });
      document.addEventListener('change', async (e) => {
        const input = e.target.closest('[data-qty-input][data-line-key]');
        if (input) {
          await this.changeLine(input.getAttribute('data-line-key'), parseInt(input.value, 10) || 0);
          return;
        }
        const prot = e.target.closest('[data-protection]');
        if (prot) await this.setProtection(prot);
      });
    },
    changeLine(key, quantity) {
      return this.run(async () => {
        this.setBusy(true);
        try {
          const data = await this.post('cart/change.js', { id: key, quantity });
          this.applySections(data.sections);
          if (data.item_count != null) this.updateCount(data.item_count);
        } catch (e) {
          console.warn('[cart] quantity change failed, restoring from server:', e && e.message);
          this.lastSync = 0;
          await this.refresh(false);
        } finally { this.setBusy(false); }
        await this.reconcile();
      });
    },
    applySections(sections) {
      this.applyPageSection(sections);
      const html = sections && sections['cart-drawer'];
      if (!html) {
        console.warn('[cart] /cart/add.js returned no cart-drawer section — falling back to a full refresh.');
        this.refresh(false);
        return;
      }
      if (!this.ensureDrawer()) {
        console.warn('[cart] no drawer element to render into — falling back to a full refresh.');
        this.refresh(false);
        return;
      }
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const fresh = tmp.querySelector('#CartDrawer');
      if (!fresh) {
        console.warn('[cart] cart-drawer section had no #CartDrawer root — falling back to a full refresh.');
        this.refresh(false);
        return;
      }
      this.drawer.innerHTML = fresh.innerHTML;
      this.mirrorState(fresh);
      this.lastSync = Date.now();
    },
    /* On /cart the page's own section comes back in the same response. */
    applyPageSection(sections) {
      const page = document.querySelector('[data-cart-page]');
      if (!page || !sections) return;
      const id = page.getAttribute('data-section-id');
      const html = id && sections[id];
      if (!html) return;
      const wrap = document.getElementById('shopify-section-' + id);
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const fresh = tmp.querySelector('#shopify-section-' + id);
      if (wrap && fresh) wrap.innerHTML = fresh.innerHTML;
    },
    setBusy(state) {
      if (this.drawer) this.drawer.classList.toggle('is-busy', state);
    },
    async add(form, button) {
      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      if (button) { button.classList.add('is-loading'); button.disabled = true; }
      try {
        const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await res.json();
        if (data.status) { // error (e.g. sold out)
          this.flash(button, data.description || 'Unavailable');
          return;
        }
        this.applySections(data.sections);
        await this.refresh(false);
        this.open();
        if (button) { button.classList.add('is-added'); setTimeout(() => button.classList.remove('is-added'), 1400); }
      } catch (e) {
        this.flash(button, 'Try again');
      } finally {
        if (button) { button.classList.remove('is-loading'); button.disabled = false; }
      }
    },
    flash(button, msg) {
      if (!button) return;
      const original = button.getAttribute('data-label') || button.textContent;
      button.textContent = msg;
      setTimeout(() => { button.textContent = original; }, 1600);
    },
  };

  /* -------------------------------------------------- AJAX add to cart */
  function initAjaxForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-ajax-cart]');
      if (!form) return;
      e.preventDefault();
      const button = form.querySelector('[type="submit"]');
      Cart.add(form, button);
    });
  }

  /* -------------------------------------------------- Sticky header */
  function initStickyHeader() {
    const header = $('[data-sticky-header]');
    if (!header) return;
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-stuck', y > 8);
      last = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -------------------------------------------------- Mobile menu */
  function initMobileMenu() {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-menu-toggle]');
      const close = e.target.closest('[data-menu-close]');
      const nav = $('[data-mobile-nav]');
      if (!nav) return;
      if (toggle) {
        const open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open);
        document.documentElement.style.overflow = open ? 'hidden' : '';
      }
      if (close || e.target.matches('[data-mobile-nav] .mobile-nav__overlay')) {
        nav.classList.remove('is-open');
        document.documentElement.style.overflow = '';
      }
    });
  }

  /* -------------------------------------------------- Predictive search */
  function initPredictiveSearch() {
    $$('[data-predictive-search]').forEach((wrap) => {
      const input = $('input[type="search"]', wrap);
      const results = $('[data-predictive-results]', wrap);
      if (!input || !results) return;
      let timer;
      const root = window.Shopify ? Shopify.routes.root : '/';
      const run = async () => {
        const q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; wrap.classList.remove('has-results'); return; }
        try {
          const res = await fetch(`${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`);
          const data = await res.json();
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) {
            results.innerHTML = `<p class="predictive__empty">No matches for "${escapeHtml(q)}" yet.</p>`;
          } else {
            results.innerHTML = products.map((p) => `
              <a class="predictive__item" href="${p.url}">
                <span class="predictive__thumb">${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
                <span class="predictive__meta">
                  <span class="predictive__title">${p.title}</span>
                  <span class="predictive__price">${p.price || ''}</span>
                </span>
              </a>`).join('');
          }
          wrap.classList.add('has-results');
        } catch (e) { /* noop */ }
      };
      input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 220); });
      input.addEventListener('focus', () => { if (input.value.trim().length >= 2) run(); });
      document.addEventListener('click', (e) => {
        if (!wrap.contains(e.target)) wrap.classList.remove('has-results');
      });
    });
  }

  /* -------------------------------------------------- Search overlay toggle
     FIX (v11): escape the shopify-section stacking trap by relocating the
     overlay element to <body> on boot. Without this, the ancestor
     `shopify-section { position:relative; z-index:1 }` creates a stacking
     context that traps the overlay's z-index, letting the sticky header
     (z:150) paint on top and intercept clicks even at z:1000.
     After relocation, position:fixed + z-index:1000 works correctly.
  ------------------------------------------------------------------ */
  function initSearchOverlay() {
    const overlay = $('[data-search-overlay]');
    if (!overlay) return;

    // ── 1. ESCAPE THE STACKING TRAP ────────────────────────────────────
    // Move the overlay out of its shopify-section ancestor to <body> so
    // position:fixed resolves against the viewport root stacking context.
    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }

    // ── 2. HELPERS ────────────────────────────────────────────────────────
    const FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';
    let lastToggle = null; // element that opened the overlay — focus returns here on close

    function getFocusable() {
      return Array.from(overlay.querySelectorAll(FOCUSABLE)).filter(
        (el) => !el.closest('[hidden]') && el.offsetParent !== null
      );
    }

    function openOverlay(triggerEl) {
      lastToggle = triggerEl || document.activeElement;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden'; // scroll lock
      const input = overlay.querySelector('input[type="search"]');
      if (input) setTimeout(() => input.focus(), 60);
    }

    function closeOverlay() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = ''; // release scroll lock
      if (lastToggle && typeof lastToggle.focus === 'function') lastToggle.focus();
      lastToggle = null;
    }

    // ── 3. OPEN / CLOSE / SCRIM CLICK ──────────────────────────────────
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-search-toggle]');
      const closeBtn = e.target.closest('[data-search-close]');

      if (toggleBtn) {
        e.preventDefault();
        openOverlay(toggleBtn);
        return;
      }
      // Close when the close button is clicked OR when clicking the scrim
      // (the overlay backdrop itself, NOT the panel inside it).
      if (closeBtn) {
        closeOverlay();
        return;
      }
      if (overlay.classList.contains('is-open') && e.target === overlay) {
        closeOverlay();
      }
    });

    // ── 4. ESCAPE KEY ──────────────────────────────────────────────────────
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
        closeOverlay();
      }
    });

    // ── 5. FOCUS TRAP ────────────────────────────────────────────────────
    // Keep Tab / Shift+Tab cycling inside the overlay while it is open so
    // focus can't bleed to the page content behind the scrim.
    overlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !overlay.classList.contains('is-open')) return;
      const focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); return; }
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* -------------------------------------------------- PDP: gallery + sticky ATC + variants */
  function initProduct() {
    const product = $('[data-product]');
    if (!product) return;

    // swipeable scroll-snap gallery; thumbnails scroll the stage
    const stage = $('[data-gallery-stage]', product);
    const thumbs = $$('[data-gallery-thumb]', product);
    function setActiveThumb(i) {
      thumbs.forEach((t) => t.classList.toggle('is-active', +t.getAttribute('data-index') === i));
    }
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const i = +thumb.getAttribute('data-index');
        const slide = stage && stage.children[i];
        if (slide) stage.scrollTo({ left: slide.offsetLeft - stage.offsetLeft, behavior: 'smooth' });
        setActiveThumb(i);
      });
    });
    if (stage && thumbs.length) {
      let raf;
      stage.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(stage.scrollLeft / stage.clientWidth);
          setActiveThumb(i);
        });
      }, { passive: true });
    }

    // variant selection -> update hidden id + price
    const variantsData = $('[data-variants-json]', product);
    let variants = [];
    if (variantsData) { try { variants = JSON.parse(variantsData.textContent); } catch (e) {} }
    const idInput = $('[data-variant-id]', product);
    const optionInputs = $$('[data-product-option]', product);
    const priceEl = $('[data-product-price]', product);
    const compareEl = $('[data-product-compare]', product);
    const stickyPrice = $('[data-sticky-price]');
    const stickyVariant = $('[data-sticky-variant]');
    const atcButtons = $$('[data-atc-text]', product);
    /* Free-shipping badge. Liquid renders the first state from the selected
       variant; this keeps it honest as the shopper switches variant. Labels
       and the threshold come off data attributes so no English or dollar
       amount is hardcoded here. */
    const shipBadge = $('[data-ship-badge]', product);
    const shipText = shipBadge ? $('[data-ship-text]', shipBadge) : null;
    const shipThreshold = shipBadge
      ? (parseInt(shipBadge.getAttribute('data-ship-threshold'), 10) || 3500)
      : 3500;

    function currentOptions() {
      const map = {};
      optionInputs.forEach((inp) => {
        if (inp.type === 'radio') { if (inp.checked) map[inp.name] = inp.value; }
        else map[inp.name] = inp.value;
      });
      return map;
    }
    function findVariant() {
      const opts = currentOptions();
      return variants.find((v) =>
        v.options.every((opt, i) => opt === opts['option' + (i + 1)])
      );
    }
    /* Size pills (and size <option>s) show a price when sizes cost different
       amounts. Liquid rendered each size's cheapest variant; here it becomes
       the exact price of that size with the shopper's OTHER selections, so a
       colour that costs more shows up on the pills before they switch. */
    const sizePriceEls = $$('[data-size-price]', product);
    function updateSizePrices() {
      if (!sizePriceEls.length) return;
      const opts = currentOptions();
      sizePriceEls.forEach((el) => {
        const idx = (parseInt(el.getAttribute('data-size-pos'), 10) || 1) - 1;
        const val = el.getAttribute('data-size-value');
        const match = variants.find((v) =>
          v.options[idx] === val && v.options.every((o, i) => i === idx || o === opts['option' + (i + 1)])
        );
        const cents = match ? match.price : parseInt(el.getAttribute('data-size-min'), 10);
        if (isNaN(cents)) return;
        const text = money(cents).replace(/<[^>]*>/g, '');
        if (el.tagName === 'OPTION') el.textContent = val + ' · ' + text;
        else el.textContent = text;
      });
    }

    function update() {
      updateSizePrices();
      const v = findVariant();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.innerHTML = money(v.price);
      if (stickyPrice) stickyPrice.innerHTML = money(v.price);
      if (stickyVariant) stickyVariant.textContent = v.title === 'Default Title' ? '' : v.title;
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.innerHTML = money(v.compare_at_price);
          compareEl.hidden = false;
        } else { compareEl.hidden = true; }
      }
      atcButtons.forEach((b) => {
        b.disabled = !v.available;
        b.textContent = v.available ? (b.getAttribute('data-atc-text') || 'Add to cart') : (b.getAttribute('data-soldout') || 'Sold out');
      });
      if (shipBadge && shipText) {
        const qualifies = v.price > shipThreshold;
        const label = qualifies
          ? shipBadge.getAttribute('data-ship-label-free')
          : shipBadge.getAttribute('data-ship-label-threshold');
        // Only touch the DOM when the wording actually changes, so switching
        // between two variants on the same side of the threshold is a no-op.
        if (label && shipText.textContent !== label) shipText.textContent = label;
        shipBadge.classList.toggle('ship-badge--free', qualifies);
      }
    }
    optionInputs.forEach((inp) => inp.addEventListener('change', update));
    if (variants.length) update();

    // sticky add-to-cart bar reveal on scroll past main ATC.
    // Uses IntersectionObserver (no per-frame layout reads). When the bar is
    // shown we also flag <body> so the mobile bottom-nav hides itself (CSS),
    // preventing the two fixed bars from stacking at the same bottom edge.
    const anchor = $('[data-atc-anchor]', product);
    const stickyBar = $('[data-sticky-atc]');

    /* Publish the bar's real height so the CSS can reserve room for it and
       park the floating controls above it. A hard-coded guess drifts as soon
       as the title wraps or the variant line appears, which is how the promo
       pill and the scroll-to-top ended up sitting on the ATC button. */
    function syncStickyHeight() {
      if (!stickyBar) return;
      const h = stickyBar.offsetHeight || 0;
      document.documentElement.style.setProperty('--stickyatc-h', h + 'px');
    }
    if (stickyBar) {
      syncStickyHeight();
      window.addEventListener('resize', () => {
        clearTimeout(syncStickyHeight._t);
        syncStickyHeight._t = setTimeout(syncStickyHeight, 120);
      }, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(syncStickyHeight).observe(stickyBar);
    }

    if (anchor && stickyBar && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', show);
          document.body.classList.toggle('sticky-atc-open', show);
          syncStickyHeight();
        });
      /* The root is stretched far below the viewport, so "not intersecting"
         can only mean "scrolled above it". Without this, jumping past the
         anchor in one step (fast fling, in-page link, restored scroll) went
         from not-intersecting-below to not-intersecting-above with no
         callback, and the bar never appeared. */
      }, { threshold: 0, rootMargin: '0px 0px 100000px 0px' });
      io.observe(anchor);
    }
    // sticky bar ATC triggers the main form
    if (stickyBar) {
      const stickyBtn = $('[data-sticky-add]', stickyBar);
      const form = $('form[data-ajax-cart]', product);
      if (stickyBtn && form) {
        stickyBtn.addEventListener('click', () => form.requestSubmit ? form.requestSubmit() : form.querySelector('[type=submit]').click());
      }
    }

    // accordions: collapse long content on mobile, expand the first on desktop
    const rows = $$('[data-pdp-row]', product);
    if (rows.length) {
      const desktop = window.matchMedia('(min-width: 750px)');
      let userTouched = false;
      rows.forEach((r) => r.addEventListener('toggle', () => { userTouched = true; }));
      const applyRows = () => { if (!userTouched) rows[0].open = desktop.matches; };
      applyRows();
      if (desktop.addEventListener) desktop.addEventListener('change', applyRows);
    }
  }

  /* -------------------------------------------------- Wishlist (local only) */
  function initWishlist() {
    const KEY = 'pp_wishlist';
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) {}
    const sync = () => $$('[data-wish]').forEach((b) => {
      b.classList.toggle('is-active', saved.includes(b.getAttribute('data-wish')));
    });
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      const id = btn.getAttribute('data-wish');
      const i = saved.indexOf(id);
      if (i > -1) saved.splice(i, 1); else saved.push(id);
      try { localStorage.setItem(KEY, JSON.stringify(saved)); } catch (e) {}
      sync();
    });
    sync();
  }

  /* -------------------------------------------------- Scroll reveal (stagger fallback) */
  function initReveal() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('in-view'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.12 });
    $$('[data-reveal]').forEach((el) => io.observe(el));
  }

  /* -------------------------------------------------- App-style bottom nav */
  function initBottomNav() {
    const nav = $('[data-bottom-nav]');
    if (!nav) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) > 6) {
        // hide when scrolling down past the fold, reveal on scroll up
        if (y > lastY && y > 140) nav.classList.add('is-hidden');
        else nav.classList.remove('is-hidden');
        lastY = y;
      }
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
  }

  /* -------------------------------------------------- Back to top */
  function initBackToTop() {
    const btn = $('[data-back-to-top]');
    if (!btn) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let ticking = false;
    const onScroll = () => {
      btn.classList.toggle('is-visible', window.scrollY > 600);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }));
  }

  /* -------------------------------------------------- Broken product images
     A product <img> whose src 404s or is blocked otherwise falls through to the
     browser's default broken-image glyph (the blue "?" box). Swap in the
     on-brand placeholder instead.

     `error` does not bubble, so this listens in the CAPTURE phase on document —
     that way it also catches images injected later by the recently-viewed rail,
     the quick-add sheet and the cart upsell, with no per-card wiring. */
  const PRODUCT_IMG_SCOPES = [
    '[data-img-fallback]',
    '.pcard__media', '.fbt__media', '.upsell__media', '.matchup__media',
    '.cart-line__media', '.cart-row__media', '.search-misc__media',
    '.quick-add__thumb', '.predictive__thumb', '.tile__media',
  ].join(',');

  /* Full-bleed heroes carry overlaid text, so the paw placeholder would sit
     under the headline. They get their own treatment: one cache-busted retry
     (most hero failures are a flaky connection, not a missing file), then the
     image is dropped and the hero falls back to a solid brand panel. */
  const HERO_IMG_SCOPES = '.collection-hero, [data-hero-fallback]';

  function failHeroImage(img) {
    const hero = img.closest(HERO_IMG_SCOPES);
    if (!hero) return false;
    if (img.dataset.imgRetried !== '1') {
      img.dataset.imgRetried = '1';
      const base = img.getAttribute('src') || '';
      if (base) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = base + (base.indexOf('?') > -1 ? '&' : '?') + 'retry=1';
        return true;
      }
    }
    img.dataset.imgFailed = '1';
    hero.classList.add('is-noimg');
    img.remove();
    return true;
  }

  /* A decorative hover image that fails is simply dropped: the primary under
     it is still fine. Replacing it with the placeholder (which is
     position:absolute and has no --two class) would paint the paw OVER a
     good product photo. */
  function failHoverImage(img) {
    if (!img.hasAttribute('data-img-hover')) return false;
    img.dataset.imgFailed = '1';
    img.remove();
    return true;
  }

  /* One cache-busted retry for a product image before giving up. A single
     card failing while its neighbours load is almost always a transient
     network or CDN blip, not a missing file, and the retry recovers it
     without the shopper ever seeing a placeholder. */
  function retryProductImage(img) {
    if (img.dataset.imgRetried === '1') return false;
    const base = img.getAttribute('src') || '';
    if (!base) return false;
    img.dataset.imgRetried = '1';
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = base + (base.indexOf('?') > -1 ? '&' : '?') + 'retry=1';
    return true;
  }

  function handleImageFailure(img, tpl) {
    if (!img || img.tagName !== 'IMG') return;
    if (img.dataset.imgFailed === '1') return;
    if (failHeroImage(img)) return;
    if (failHoverImage(img)) return;
    if (!img.closest(PRODUCT_IMG_SCOPES)) return;
    if (retryProductImage(img)) return;
    img.dataset.imgFailed = '1';
    let node;
    if (tpl && tpl.content && tpl.content.firstElementChild) {
      node = tpl.content.firstElementChild.cloneNode(true);
    } else {
      node = document.createElement('span');
      node.className = 'img-fallback';
    }
    if (img.parentNode) img.parentNode.replaceChild(node, img);
  }

  function initImageFallback() {
    const tpl = document.getElementById('PPImgFallback');

    /* This script is deferred, so an eager image (the collection hero) can
       fail BEFORE the listener below exists and would keep the browser's
       broken glyph forever. Sweep once now and once after load for images
       that already finished loading with nothing in them. */
    const sweep = () => {
      $$('img').forEach((img) => {
        /* Skip images that were never requested. Hover images are
           display:none on touch screens and lazy, so some engines report
           them complete with naturalWidth 0 even though nothing failed. */
        if (img.hasAttribute('data-img-hover')) return;
        if (img.loading === 'lazy' && img.getClientRects().length === 0) return;
        if (img.complete && img.getAttribute('src') && img.naturalWidth === 0) handleImageFailure(img, tpl);
      });
    };
    sweep();
    window.addEventListener('load', sweep, { once: true });

    document.addEventListener('error', (e) => {
      const img = e.target;
      if (!img || img.tagName !== 'IMG') return;
      // One path for every failure, so hero retry, hover removal and the
      // product retry all apply no matter which route reported it.
      handleImageFailure(img, tpl);
    }, true);
  }

  /* Markup for the same placeholder, for the cards that are built as HTML
     strings (recently viewed, cart upsell, quick-add thumb). */
  function imgFallbackHtml() {
    const tpl = document.getElementById('PPImgFallback');
    return tpl ? tpl.innerHTML : '<span class="img-fallback"></span>';
  }

  /* -------------------------------------------------- Quick-add chooser */
  const QuickAdd = {
    modal: null, content: null, product: null, selected: [],
    init() {
      this.modal = $('[data-quick-add-modal]');
      this.content = this.modal && $('[data-quick-add-content]', this.modal);
      if (!this.modal) return;
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-add]');
        if (trigger) { e.preventDefault(); this.open(trigger.getAttribute('data-quick-add')); }
        if (e.target.closest('[data-quick-add-close]') || e.target.matches('.quick-add__overlay')) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this.close();
      });
    },
    open(handle) {
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this.load(handle);
    },
    close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    },
    async load(handle) {
      this.content.innerHTML = '<div class="quick-add__skel" aria-hidden="true"><span class="skeleton" style="height:72px;border-radius:16px"></span><span class="skeleton" style="width:60%"></span><span class="skeleton" style="width:40%"></span></div>';
      const root = window.Shopify ? Shopify.routes.root : '/';
      try {
        const res = await fetch(`${root}products/${handle}.js`);
        this.render(await res.json());
      } catch (e) {
        this.content.innerHTML = '<p class="predictive__empty">Couldn\'t load options. Open the product page instead.</p>';
      }
    },
    optionValues(idx) {
      const seen = [];
      this.product.variants.forEach((v) => { if (!seen.includes(v.options[idx])) seen.push(v.options[idx]); });
      return seen;
    },
    render(p) {
      this.product = p;
      const first = p.variants.find((v) => v.available) || p.variants[0];
      this.selected = first.options.slice();
      const img = `<span class="quick-add__thumb">${p.featured_image
        ? `<img src="${withWidth(p.featured_image, 200)}" alt="" width="72" height="72" loading="lazy" decoding="async" data-img-fallback>`
        : imgFallbackHtml()}</span>`;
      let optsHtml = '';
      p.options.forEach((option, idx) => {
        const values = this.optionValues(idx).map((val) =>
          `<button type="button" class="quick-add__value" data-qa-option="${idx}" data-qa-value="${escapeHtml(val)}">${escapeHtml(val)}</button>`).join('');
        optsHtml += `<div class="quick-add__option"><span class="quick-add__option-label">${escapeHtml(option.name)}</span><div class="quick-add__values">${values}</div></div>`;
      });
      this.content.innerHTML = `
        <div class="quick-add__head">
          ${img}
          <div>
            <div class="quick-add__title">${escapeHtml(p.title)}</div>
            <div class="quick-add__price" data-qa-price></div>
          </div>
        </div>
        ${optsHtml}
        <div class="quick-add__foot">
          <button type="button" class="btn btn--primary btn--lg btn--full" data-qa-add>Add to cart</button>
          <ul class="trust-micro"><li>${'✓'} Secure checkout</li><li>${'✓'} Fast US shipping</li><li>${'✓'} 30-day returns</li></ul>
        </div>`;
      this.bind();
      this.update();
    },
    bind() {
      $$('[data-qa-option]', this.content).forEach((btn) => {
        btn.addEventListener('click', () => {
          this.selected[+btn.getAttribute('data-qa-option')] = btn.getAttribute('data-qa-value');
          this.update();
        });
      });
      const add = $('[data-qa-add]', this.content);
      if (add) add.addEventListener('click', () => this.add(add));
    },
    currentVariant() {
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i]));
    },
    update() {
      $$('[data-qa-option]', this.content).forEach((btn) => {
        const idx = +btn.getAttribute('data-qa-option');
        const val = btn.getAttribute('data-qa-value');
        btn.classList.toggle('is-active', this.selected[idx] === val);
        const possible = this.product.variants.some((v) => v.options[idx] === val && v.available);
        btn.classList.toggle('is-unavailable', !possible);
      });
      const v = this.currentVariant();
      const priceEl = $('[data-qa-price]', this.content);
      const add = $('[data-qa-add]', this.content);
      if (v && priceEl) {
        let html = money(v.price);
        if (v.compare_at_price && v.compare_at_price > v.price) html += ` <span class="money--was">${money(v.compare_at_price)}</span>`;
        priceEl.innerHTML = html;
      }
      if (add) {
        const ok = v && v.available;
        add.disabled = !ok;
        add.textContent = ok ? 'Add to cart' : (v ? 'Sold out' : 'Unavailable');
      }
    },
    async add(button) {
      const v = this.currentVariant();
      if (!v || !v.available) return;
      button.classList.add('is-loading'); button.disabled = true;
      const root = window.Shopify ? Shopify.routes.root : '/';
      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }], sections: 'cart-drawer', sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (data.status) { button.textContent = 'Unavailable'; return; }
        Cart.applySections(data.sections);
        await Cart.refresh(false);
        this.close();
        Cart.open();
      } catch (e) { button.textContent = 'Try again'; }
      finally { button.classList.remove('is-loading'); button.disabled = false; }
    },
  };

  /* Products tagged `ad-excluded` must never appear in a recommendation block
     or anything a feed could scrape. They stay reachable by direct URL and by
     search — this only suppresses them from rec output. Both /products/{h}.js
     and /recommendations/products.json return `tags`, so one check covers the
     two JS surfaces (recently viewed, cart-drawer complementary). */
  const AD_EXCLUDED_TAG = 'ad-excluded';
  function isAdExcluded(p) {
    if (!p || !p.tags) return false;
    const tags = Array.isArray(p.tags) ? p.tags : String(p.tags).split(',');
    return tags.some((t) => String(t).trim().toLowerCase() === AD_EXCLUDED_TAG);
  }

  /* -------------------------------------------------- Recently viewed */
  const RecentlyViewed = {
    KEY: 'pp_recently_viewed',
    init() {
      let list = [];
      try { list = JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch (e) {}
      const handleEl = $('[data-product-handle]');
      const current = handleEl ? handleEl.getAttribute('data-product-handle') : null;
      if (current) {
        list = list.filter((h) => h !== current);
        list.unshift(current);
        list = list.slice(0, 12);
        try { localStorage.setItem(this.KEY, JSON.stringify(list)); } catch (e) {}
      }
      this.render(list, current);
    },
    async render(list, current) {
      const wraps = $$('[data-recently-viewed]');
      if (!wraps.length) return;
      const handles = list.filter((h) => h !== current).slice(0, 8);
      const sections = wraps.map((w) => w.closest('[data-recently-section]'));
      if (!handles.length) { sections.forEach((s) => s && (s.hidden = true)); return; }
      const skel = Array.from({ length: Math.min(handles.length, 4) }, () =>
        '<div class="pcard-skel"><div class="pcard-skel__media skeleton"></div><div class="pcard-skel__body"><span class="pcard-skel__line skeleton"></span><span class="pcard-skel__line pcard-skel__line--sm skeleton"></span></div></div>').join('');
      wraps.forEach((w, i) => { w.innerHTML = skel; if (sections[i]) sections[i].hidden = false; });
      const root = window.Shopify ? Shopify.routes.root : '/';
      const products = await Promise.all(handles.map((h) =>
        fetch(`${root}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      const valid = products.filter(Boolean).filter((p) => !isAdExcluded(p));
      if (!valid.length) { sections.forEach((s) => s && (s.hidden = true)); return; }
      const html = valid.map(rvCard).join('');
      wraps.forEach((w, i) => { w.innerHTML = html; if (sections[i]) sections[i].hidden = false; });
    },
  };

  function rvCard(p) {
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const media = p.featured_image
      ? `<img class="pcard__img pcard__img--only" src="${withWidth(p.featured_image, 600)}" alt="${escapeHtml(p.title)}" width="300" height="300" loading="lazy" decoding="async" data-img-fallback>`
      : imgFallbackHtml();
    const price = onSale
      ? `<span class="money money--sale">${money(p.price)}</span> <span class="money money--was">${money(p.compare_at_price)}</span>`
      : `<span class="money">${money(p.price)}</span>`;
    return `<article class="pcard"><a class="pcard__media" href="${p.url}" aria-label="${escapeHtml(p.title)}">${media}</a>
      <div class="pcard__body"><a class="pcard__title" href="${p.url}">${escapeHtml(p.title)}</a><div class="pcard__price">${price}</div></div></article>`;
  }

  /* -------------------------------------------------- PDP gallery dots + zoom */
  function initGalleryExtras() {
    const product = $('[data-product]');
    if (!product) return;
    const stage = $('[data-gallery-stage]', product);
    const dots = $$('[data-gallery-dot]', product);
    if (stage && dots.length) {
      const setDot = (i) => dots.forEach((d) => d.classList.toggle('is-active', +d.getAttribute('data-index') === i));
      dots.forEach((dot) => dot.addEventListener('click', () => {
        const i = +dot.getAttribute('data-index');
        const slide = stage.children[i];
        if (slide) stage.scrollTo({ left: slide.offsetLeft - stage.offsetLeft, behavior: 'smooth' });
        setDot(i);
      }));
      let raf;
      stage.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => setDot(Math.round(stage.scrollLeft / stage.clientWidth)));
      }, { passive: true });
    }
    // tap-to-zoom lightbox (built on demand, no library)
    let lightbox;
    const openZoom = (src) => {
      if (!lightbox) {
        lightbox = document.createElement('div');
        lightbox.className = 'lightbox';
        lightbox.innerHTML = '<button type="button" class="lightbox__close" aria-label="Close">✕</button><img alt="">';
        document.body.appendChild(lightbox);
        lightbox.addEventListener('click', (e) => {
          if (e.target === lightbox || e.target.closest('.lightbox__close')) {
            lightbox.classList.remove('is-open');
            document.documentElement.style.overflow = '';
          }
        });
      }
      lightbox.querySelector('img').src = src;
      lightbox.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
    };
    $$('[data-zoom-src]', product).forEach((slide) => {
      slide.addEventListener('click', () => openZoom(slide.getAttribute('data-zoom-src')));
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox && lightbox.classList.contains('is-open')) {
        lightbox.classList.remove('is-open');
        document.documentElement.style.overflow = '';
      }
    });
  }

  /* -------------------------------------------------- Free-shipping bars */
  const Ship = {
    update(totalCents) {
      const t = window.PP_SHIP_THRESHOLD || 0;
      if (!t) return;
      const total = parseInt(totalCents, 10) || 0;
      const met = total >= t;
      const pct = Math.min(100, Math.round((total * 100) / t));
      $$('[data-ship]').forEach((w) => {
        const fill = $('[data-ship-fill]', w);
        const text = $('[data-ship-text]', w);
        if (fill) fill.style.width = pct + '%';
        w.classList.toggle('is-met', met);
        const nearCents = parseInt(w.getAttribute('data-near'), 10) || 0;
        const near = !met && (t - total) <= nearCents;
        w.classList.toggle('is-near', near);
        if (text) {
          /* "$35" not "$35.00" — matches the server-rendered copy. */
          const amount = money(t - total).replace(/[.,]00(?!\d)/, '');
          if (met) text.textContent = w.getAttribute('data-msg-met') || '';
          else text.textContent = (w.getAttribute(near ? 'data-msg-near' : 'data-msg-add') || '').replace('{amount}', amount);
        }
      });
    },
  };
  window.PawsomeShip = Ship;

  /* -------------------------------------------------- Cart upsell (recs + fallback) */
  const CartUpsell = {
    /* Pair mode (cart-drawer.liquid): the slot holds the mini cigar and/or the
       money roll and shows exactly one. A fresh coin flip on every drawer
       OPEN; re-renders while it is open (an add, a quantity change, the
       stale-sync) keep the same pick so the row does not swap under a thumb. */
    picked: null,
    pickPair(drawer, reroll) {
      const box = drawer && $('[data-upsell-pair]', drawer);
      if (!box) return false;
      const rows = $$('[data-upsell-pair-row]', box);
      if (!rows.length) return true;
      const handles = rows.map((r) => r.getAttribute('data-handle'));
      if (reroll || handles.indexOf(this.picked) === -1) {
        this.picked = rows.length > 1 ? handles[Math.random() < 0.5 ? 0 : 1] : handles[0];
      }
      rows.forEach((r) => r.classList.toggle('is-picked', r.getAttribute('data-handle') === this.picked));
      return true;
    },
    async enhance(drawer) {
      if (!drawer) return;
      // Pair mode shows its one product and nothing else.
      if (this.pickPair(drawer, false)) return;
      const box = $('[data-upsell]', drawer);
      if (!box) return;
      const list = $('[data-upsell-list]', box);
      const first = box.getAttribute('data-first');
      if (!list || !first) return;
      const inCart = (box.getAttribute('data-incart') || '');
      const root = window.Shopify ? Shopify.routes.root : '/';
      let products;
      try {
        const res = await fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(first)}&limit=6&intent=complementary`);
        if (!res.ok) return;
        products = (await res.json()).products || [];
      } catch (e) { return; }
      // Drawer may have re-rendered while we were fetching — bail if detached.
      if (!products.length || !list.isConnected) return;
      const have = $$('[data-handle]', list).map((r) => r.getAttribute('data-handle'));
      for (const p of products) {
        if (!p || !p.handle) continue;
        if (isAdExcluded(p)) continue;
        if (inCart.indexOf(p.handle) > -1) continue;
        if (have.indexOf(p.handle) > -1) continue;
        const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
        if (!variant) continue;
        list.insertAdjacentHTML('afterbegin', this.rowHtml(p, variant, root));
        have.unshift(p.handle);
      }
      // Keep the list capped at 3 visible rows.
      const rows = $$('.upsell__row', list);
      rows.slice(3).forEach((r) => r.remove());
    },
    rowHtml(p, variant, root) {
      const img = p.featured_image
        ? `<img src="${withWidth(p.featured_image, 120)}" alt="" width="52" height="52" loading="lazy" decoding="async" data-img-fallback>`
        : imgFallbackHtml();
      return `<li class="upsell__row" data-handle="${escapeHtml(p.handle)}">
        <a class="upsell__media" href="${escapeHtml(p.url)}" tabindex="-1" aria-hidden="true">${img}</a>
        <div class="upsell__info">
          <a class="upsell__title" href="${escapeHtml(p.url)}">${escapeHtml(truncate(p.title, 40))}</a>
          <span class="upsell__price">${money(p.price)}</span>
        </div>
        <form action="${root}cart/add" method="post" data-ajax-cart class="upsell__form">
          <input type="hidden" name="id" value="${variant.id}">
          <button type="submit" class="upsell__add" data-label="Add" aria-label="Add ${escapeHtml(p.title)} to cart">+ Add</button>
        </form>
      </li>`;
    },
  };

  /* -------------------------------------------------- Complete the set (FBT) */
  function initFBT() {
    const w = $('[data-fbt]');
    if (!w) return;
    const items = $$('[data-fbt-item]', w);
    const totalEl = $('[data-fbt-total]', w);
    const addBtn = $('[data-fbt-add]', w);
    const promoEl = $('[data-fbt-promo]', w);
    const promoMin = promoEl ? (parseInt(promoEl.getAttribute('data-fbt-promo-min'), 10) || 0) : 0;
    const root = window.Shopify ? Shopify.routes.root : '/';
    const label = addBtn ? (addBtn.getAttribute('data-label') || 'Add {count} to cart') : '';
    function recalc() {
      let cents = 0, n = 0;
      items.forEach((i) => { if (i.checked) { cents += parseInt(i.getAttribute('data-price'), 10) || 0; n += 1; } });
      if (totalEl) totalEl.innerHTML = money(cents);
      if (addBtn) {
        addBtn.textContent = label.replace('{count}', n);
        addBtn.disabled = n === 0;
      }
      // Only surface the 3-for-2 line when the full qualifying set is selected.
      if (promoEl) promoEl.style.display = (promoMin && n >= promoMin) ? '' : 'none';
    }
    items.forEach((i) => i.addEventListener('change', recalc));
    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const picks = items.filter((i) => i.checked).map((i) => ({ id: parseInt(i.value, 10), quantity: 1 }));
        if (!picks.length) return;
        addBtn.classList.add('is-loading'); addBtn.disabled = true;
        try {
          const res = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: picks, sections: 'cart-drawer', sections_url: window.location.pathname }),
          });
          const data = await res.json();
          if (data.status) { addBtn.textContent = 'Unavailable'; return; }
          Cart.applySections(data.sections);
          await Cart.refresh(false);
          Cart.open();
        } catch (e) { addBtn.textContent = 'Try again'; }
        finally { addBtn.classList.remove('is-loading'); addBtn.disabled = false; recalc(); }
      });
    }
    recalc();
  }

  /* -------------------------------------------------- helpers */
  function truncate(s, n) {
    s = String(s || '');
    return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;
  }
  function withWidth(url, w) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* -------------------------------------------------- boot */
  function boot() {
    initImageFallback();
    Cart.init();
    initAjaxForms();
    initStickyHeader();
    initMobileMenu();
    initPredictiveSearch();
    initSearchOverlay();
    initProduct();
    initGalleryExtras();
    initWishlist();
    initReveal();
    initBottomNav();
    initBackToTop();
    QuickAdd.init();
    RecentlyViewed.init();
    initFBT();
    // Sync the shipping bars (esp. the PDP sticky bar) to the current cart total.
    if (Cart.drawer) Ship.update(parseInt(Cart.drawer.getAttribute('data-cart-total') || '0', 10));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // expose for inline section scripts if needed
  window.PawsomeCart = Cart;
})();
